// Overview pins — a list of scopes you've pinned to the rail, each one a
// full-screen vantage point you can flip to and back.
//
// Stored in the URL (?pins=<lens>~<scope>;<lens>~<scope>;…), like the rest of
// the dashboard's view state — per-tab and shareable. Each entry carries its
// own locked lens (the tab that pin remembers). Order = add order. Pins are
// addressed by their encoded scope (unique within the list).
//
// ?pins is written via writeUrlParam (bare replaceState) and changes are
// announced through the in-memory listener set — react-router doesn't
// re-render on replaceState, so the subscription is what refreshes the UI.
// The lens never contains "~" (split on the first one) and an encoded scope
// never contains ";" (that separates tabs), so those are safe separators.

import { readUrlParams, writeUrlParam } from "./urlParams.js";

const PARAM = "pins";

const listeners = new Set();
function notify() {
    for (const fn of listeners) {
        try { fn(); } catch { /* noop */ }
    }
}

export function subscribePins(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

function parsePin(token) {
    const i = token.indexOf("~");
    if (i < 0) return null;
    return { lens: token.slice(0, i) || "endpoints", scope: token.slice(i + 1) };
}

function encodePin(pin) {
    return `${pin.lens || "endpoints"}~${pin.scope}`;
}

export function getPins() {
    try {
        const raw = readUrlParams().get(PARAM);
        if (!raw) return [];
        return raw.split(";").map(parsePin).filter(Boolean);
    } catch {
        return [];
    }
}

function writePins(pins) {
    writeUrlParam(PARAM, pins.length ? pins.map(encodePin).join(";") : null);
    notify();
}

export function indexOfPin(scope) {
    return getPins().findIndex((p) => p.scope === scope);
}

export function isPinned(scope) {
    return indexOfPin(scope) >= 0;
}

export function getPin(scope) {
    return getPins().find((p) => p.scope === scope) || null;
}

// Append a pin (no-op if this scope is already pinned).
export function addPin({ scope, lens }) {
    if (typeof scope !== "string") return;
    const pins = getPins();
    if (pins.some((p) => p.scope === scope)) return;
    writePins([...pins, { scope, lens: typeof lens === "string" && lens ? lens : "endpoints" }]);
}

export function removePin(scope) {
    const pins = getPins();
    const next = pins.filter((p) => p.scope !== scope);
    if (next.length !== pins.length) writePins(next);
}

// Update the pin matching `scope` — used to lock its lens, or to re-scope it
// (adjusting what a pin points at) while keeping its slot/order.
export function updatePin(scope, patch) {
    const pins = getPins();
    const i = pins.findIndex((p) => p.scope === scope);
    if (i < 0) return;
    const next = pins.slice();
    next[i] = { ...next[i], ...patch };
    writePins(next);
}

// Token form (lens~scope) used by App as the active-pin marker (?pin) — a
// non-empty value even for the overview scope (which encodes to "").
export function pinToken(pin) {
    return encodePin(pin);
}
export function parsePinToken(token) {
    return token ? parsePin(token) : null;
}
